import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiSettings } from 'react-icons/fi';
import { useAuthStore } from '../../store/authStore';
import { fetchProfileStats } from '../../lib/profileStats';
import { isTestAuthToken } from '../../lib/testAuth';
import { useFormat } from '../../hooks/useFormat';
import { displayMap } from '../../lib/format';
import { useLanguage } from '../../contexts/LanguageContext';
import { Action, request } from '../MVP/shared';
import MatchRow from '../../components/UI/MatchRow';
import MapThumb from '../../components/UI/MapThumb';
import { matchState, matchTitle } from '../../components/UI/matchLabels';
import { matchOutcome, StatsMatch } from '../Stats/statsUtils';
import PlayerHero from './PlayerHero';

const Page = styled.div`
  width: 100%;
  min-width: 0;
  display: grid;
  gap: 24px;
  padding-top: 16px;
  @media (max-width: 767px) {
    gap: 16px;
    padding-top: 8px;
  }
`;
const Surface = styled.section`
  min-width: 0;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
  h2 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 16px;
    margin-bottom: 12px;
    a {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      white-space: nowrap;
      font-size: 14px;
      font-weight: 600;
      color: ${(p) => p.theme.colors.textSecondary};
      &:hover {
        color: ${(p) => p.theme.colors.text};
        text-decoration: underline;
      }
    }
  }
`;
const Summary = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 20px 24px;
  > div {
    display: grid;
    gap: 2px;
  }
  dt {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  dd {
    font-size: 28px;
    line-height: 36px;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }
  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const Two = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;
const Fav = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  strong {
    display: block;
    font-size: 20px;
    line-height: 28px;
  }
  small {
    display: block;
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Dim = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textMuted};
`;
const Start = styled.div`
  display: grid;
  justify-items: start;
  gap: 8px;
  h2 {
    font-size: 20px;
  }
  p {
    max-width: 52ch;
    font-size: 15px;
    line-height: 22px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;

const Profile: React.FC = () => {
  const { t } = useLanguage();
  const fmt = useFormat();
  const user = useAuthStore((s) => s.user);
  const [recent, setRecent] = useState<any[] | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    if (isTestAuthToken(localStorage.getItem('auth-token'))) return;
    let cancelled = false;
    (async () => {
      try {
        const stats = await fetchProfileStats();
        if (cancelled) return;
        const cur = useAuthStore.getState().user;
        if (cur) useAuthStore.getState().updateUser({ stats: { ...cur.stats, ...stats } });
      } catch {
        /* API недоступен — остаются кэшированные показатели */
      }
    })();
    request('/stats/me/matches?page=1&pageSize=3')
      .then((d) => !cancelled && setRecent(d.items || []))
      .catch(() => !cancelled && setRecent([]));
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const s = user?.stats;
  const played = s?.totalMatches ?? 0;
  const hasMatches = played > 0;
  const best = s?.bestMap && s.bestMap !== 'N/A' ? s.bestMap : null;

  return (
    <Page>
      <PlayerHero
        name={user?.username || t('header.userDefault')}
        avatar={user?.avatar}
        background={user?.background}
        isPremium={!!user?.isPremium}
        elo={user?.elo}
        meta={user?.rank}
        actions={
          <Action as={Link} to="/settings">
            <FiSettings aria-hidden="true" />
            {t('profile.settings_btn')}
          </Action>
        }
      />

      {!hasMatches ? (
        <Surface>
          <Start>
            <h2>{t('profile.newbie_title')}</h2>
            <p>{t('profile.newbie_text')}</p>
            <Action as={Link} to="/" $variant="primary" style={{ marginTop: 8 }}>
              {t('profile.to_game')}
            </Action>
          </Start>
        </Surface>
      ) : (
        <>
          <Surface>
            <Summary>
              <div>
                <dt>{t('profile.stats.matches')}</dt>
                <dd>{fmt.number(played, 0)}</dd>
              </div>
              <div>
                <dt>{t('profile.stats.wins')}</dt>
                <dd>{fmt.number(s?.wins, 0)}</dd>
              </div>
              <div>
                <dt>{t('profile.stats.losses')}</dt>
                <dd>{fmt.number(s?.losses, 0)}</dd>
              </div>
              {typeof s?.draws === 'number' && (
                <div>
                  <dt>{t('profile.stats.draws')}</dt>
                  <dd>{fmt.number(s.draws, 0)}</dd>
                </div>
              )}
              <div>
                <dt>{t('profile.stats.winrate')}</dt>
                <dd>{fmt.percent(s?.winRate, 1)}</dd>
              </div>
            </Summary>
          </Surface>

          <Two>
            <Surface>
              <div className="head">
                <h2>{t('profile.indicators')}</h2>
              </div>
              <Summary style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <div>
                  <dt>{t('stats.kd_ratio')}</dt>
                  <dd>{s?.kd ? fmt.number(s.kd, 2) : <Dim>{t('profile.no_data')}</Dim>}</dd>
                </div>
                <div>
                  <dt>MVP</dt>
                  <dd>{typeof s?.mvps === 'number' ? fmt.number(s.mvps, 0) : <Dim>{t('profile.no_data')}</Dim>}</dd>
                </div>
              </Summary>
            </Surface>
            <Surface>
              <div className="head">
                <h2>{t('profile.metrics.best_map')}</h2>
              </div>
              {best ? (
                <Fav>
                  <MapThumb map={best} width={96} height={60} />
                  <div>
                    <strong>{displayMap(best)}</strong>
                    <small>{t('profile.metrics.best_map_desc')}</small>
                  </div>
                </Fav>
              ) : (
                <Dim>{t('profile.no_data')}</Dim>
              )}
            </Surface>
          </Two>

          {recent && recent.length > 0 && (
            <Surface aria-labelledby="profile-recent">
              <div className="head">
                <h2 id="profile-recent">{t('profile.recent')}</h2>
                <Link to="/stats">{t('profile.full_stats')}</Link>
              </div>
              <ul style={{ listStyle: 'none', display: 'grid', gap: 2, margin: '0 -8px' }}>
                {recent.map((m) => {
                  const o = matchOutcome({ status: m.status, score: [m.score_ct ?? 0, m.score_t ?? 0], team: m.team } as unknown as StatsMatch);
                  const own: [number, number] = m.team === 'T' ? [m.score_t ?? 0, m.score_ct ?? 0] : [m.score_ct ?? 0, m.score_t ?? 0];
                  return (
                    <MatchRow
                      key={m.id}
                      to={'/match/' + m.id}
                      map={m.map || null}
                      title={matchTitle(t, m.map, m.status)}
                      date={fmt.date(m.created_at)}
                      outcome={m.status === 'cancelled' ? 'cancelled' : o}
                      stateLabel={matchState(t, m.status, o)}
                      score={o === 'pending' ? null : own}
                    />
                  );
                })}
              </ul>
            </Surface>
          )}
        </>
      )}
    </Page>
  );
};

export default Profile;
